/**
 * @fileoverview What has been written about this class's meetings before they
 * happen — the organization's brief and the instructors' own plans — gathered
 * at the foot of the class.
 *
 * The brief is written on the meeting (the Calendar tab's meeting editor, O7),
 * because it is about one session and belongs to its definition. This card is
 * where the org sees all of them at once, next to what the instructors are
 * planning, which answers "is this session ready" without opening every meeting.
 *
 * **What is still ahead only.** Prep is forward-looking: a brief on a meeting
 * that has happened is part of that meeting, still on the Calendar tab, and
 * gathering a term's worth of them here would bury next Tuesday under August.
 *
 * **Last on the page, and absent when there is nothing.** Most classes never
 * need a brief. A card that was always there, usually empty, would be a
 * permanently unanswered question on a page whose job is to say what still
 * needs doing.
 *
 * **Both authors, always labelled.** An administrator should never have to
 * guess whose words they are reading. The instructor is told on their composer
 * that their plan shows here: preparation is a working conversation about a
 * session, not private correspondence. What stays closed is the lesson itself —
 * a plan that has become a lesson has left this list, and reading it takes the
 * `lesson_notes` viewer grant. Nothing here is ever shown to a family.
 */

import { Card, Eyebrow, ListRow, Tag } from "@/components/studio/primitives";

/** "Tue, Sep 24" from a YYYY-MM-DD, without tripping over UTC parsing. */
function dayLabel(ymd) {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  if (!y || !m || !d) return ymd || "Undated";
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

/**
 * @param {object} props
 * @param {Array} props.meetings  the class's meetings; any with a `prep_note` is the org's brief
 * @param {Array} props.plans     the instructors' plans, `{occurrence_id, date, body, author_name}`
 * @param {Function} [props.onOpenMeeting] opens a meeting's editor, for the org's own rows
 */
export default function ClassPrepNotesCard({ meetings = [], plans = [], onOpenMeeting }) {
  const rows = [
    ...meetings
      .filter((m) => (m.prep_note || "").trim() && m.status !== "cancelled")
      .map((m) => ({ key: `org:${m.occurrence_id}`, date: m.date, body: m.prep_note, org: true, meeting: m })),
    ...plans
      .filter((p) => (p.body || "").trim())
      .map((p) => ({
        key: `plan:${p.occurrence_id}:${p.user_id || ""}`,
        date: p.date,
        body: p.body,
        author: p.author_name,
        org: false,
      })),
  ].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")) || Number(b.org) - Number(a.org));

  if (rows.length === 0) return null;

  return (
    <div className="mt-6" data-testid="class-prep-notes">
      <Card padding={0}>
        <div className="flex items-center justify-between gap-3 px-[18px] pb-2 pt-4">
          <Eyebrow>Prep notes</Eyebrow>
          <span className="font-sans text-[13px] text-[var(--text-tertiary)]">
            {rows.length === 1 ? "1 note" : `${rows.length} notes`}
          </span>
        </div>
        {rows.map((row, i) => (
          <ListRow
            key={row.key}
            borderBottom={i < rows.length - 1}
            wrapText
            title={dayLabel(row.date)}
            subtitle={row.body}
            meta={row.org ? undefined : row.author}
            trailing={row.org ? <Tag accent>Ours</Tag> : <Tag>Instructor&rsquo;s plan</Tag>}
            onPress={row.org && onOpenMeeting ? () => onOpenMeeting(row.meeting) : undefined}
            data-testid={`class-prep-note-${row.key}`}
          />
        ))}
      </Card>
      <p className="mt-2 max-w-3xl font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]">
        Yours appear on the instructor&rsquo;s session and while they write it up; theirs are the plans they&rsquo;re
        working from. Neither is sent to families or appears in a lesson summary.
      </p>
    </div>
  );
}
